import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/_components/dialog";
import { Button } from "@/components/_components/button";
import { useGetFailureTransactionPayload } from "@/lib/api/hooks";
import { Loader2 } from "lucide-react";
import { JsonValue, JsonObject, PayloadData } from "@/types/common";

interface PayloadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  transactionId: number;
  correlationId?: string;
}

const FailureTransactionPayloadDialog = ({
  isOpen,
  onClose,
  transactionId,
  correlationId,
}: PayloadDialogProps) => {
  const { data: payloadData, isLoading, error, refetch } = useGetFailureTransactionPayload(
    transactionId,
    {
      query: {
        enabled: isOpen && !!transactionId,
      },
    }
  );



  const renderPayloadTable = (data: PayloadData | undefined) => {
    if (!data || (typeof data !== 'object' && typeof data !== 'string')) {
      return (
        <div className="text-center py-4 text-gray-500">
          No payload data available
        </div>
      );
    }

    // If data is a string, try to parse it as JSON
    let parsedData: JsonObject;
    if (typeof data === 'string') {
      try {
        parsedData = JSON.parse(data) as JsonObject;
      } catch (e) {
        // If parsing fails, treat it as plain text
        return (
          <div className="overflow-auto">
            <pre className="whitespace-pre-wrap font-mono text-xs break-words p-4 bg-gray-50 rounded">{data}</pre>
          </div>
        );
      }
    } else {
      parsedData = data;
    }

    const renderValue = (value: JsonValue): string => {
      if (value === null || value === undefined) {
        return 'null';
      }
      if (typeof value === 'boolean') {
        return String(value);
      }
      if (typeof value === 'number') {
        return String(value);
      }
      if (typeof value === 'object') {
        return JSON.stringify(value, null, 2);
      }
      return String(value);
    };

    // Convert object to array of key-value pairs
    const entries = Object.entries(parsedData);

    if (entries.length === 0) {
      return (
        <div className="text-center py-4 text-gray-500">
          No payload data available
        </div>
      );
    }

    return (
      <div className="w-full">
        <div className="overflow-auto max-h-full">
          <table className="w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">
                  Key
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Value
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {entries.map(([key, value], index) => (
                <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 align-top border-r border-gray-200">
                    <div className="break-words min-w-0">{key}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 align-top">
                    <div className="min-w-0">
                      <pre className="whitespace-pre-wrap font-mono text-xs break-words overflow-wrap-anywhere">
                        {renderValue(value)}
                      </pre>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl w-[90vw] h-[85vh] flex flex-col p-0">
        <DialogHeader className="flex-shrink-0 px-6 py-4 border-b">
          <DialogTitle className="text-lg font-semibold">
            Transaction Payload Details
          </DialogTitle>
          <div className="text-sm text-gray-600 mt-2 space-y-1">
            {correlationId && (
              <div><span className="font-medium">Correlation ID:</span> {correlationId}</div>
            )}
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-hidden p-6">
          <div className="h-full border rounded-lg bg-white">
            <div className="h-full flex flex-col">
              <div className="flex-shrink-0 px-4 py-3 bg-gray-50 border-b">
                <h3 className="text-base font-medium text-gray-900">Payload Data</h3>
              </div>
              <div className="flex-1 overflow-auto p-4">
                {isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin mr-2" />
                    <span>Loading payload data...</span>
                  </div>
                ) : error ? (
                  <div className="text-center py-12">
                    <p className="text-red-600 mb-4">
                      Failed to load payload data: {error.message}
                    </p>
                    <Button onClick={() => refetch()} variant="outline">
                      Try Again
                    </Button>
                  </div>
                ) : (
                  renderPayloadTable(payloadData?.data)
                )}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FailureTransactionPayloadDialog;